import { CartItem, CartItemType, EventPassEntry, ShoppingCart } from '@/types';
import { trackAddToCart } from '@/hooks/usePostHogTracking';
import { toast } from 'sonner';
import i18n from '@/i18n';
import posthogLib from 'posthog-js';

const CART_STORAGE_KEY = 'equestrian_cart';

// Swiss pricing constants (display only — authoritative prices come from Stripe at checkout)
export const PRICES = {
  SINGLE_PHOTO: 13, // CHF 13.- per photo
  COLLECTION: 70,   // CHF 70.- for entire participant collection (one race)
  EVENT_PASS: 99,   // CHF 99.- every photo of one rider across a whole event
} as const;

/** Minimum number of classes a rider needs at an event for the pass to make sense. */
export const EVENT_PASS_MIN_RACES = 2;

const isValidCartItem = (item: any): item is CartItem => {
  return (
    item &&
    typeof item.participantId === 'string' &&
    item.participantId.length > 0 &&
    typeof item.type === 'string' &&
    (item.type === 'single' || item.type === 'collection' || item.type === 'event_pass') &&
    typeof item.price === 'number' &&
    item.price > 0 &&
    typeof item.quantity === 'number' &&
    item.quantity > 0 &&
    typeof item.participantName === 'string' &&
    typeof item.raceCategory === 'string' &&
    typeof item.eventName === 'string' &&
    typeof item.eventId === 'string' &&
    typeof item.raceId === 'string' &&
    (item.type !== 'single' || (typeof item.photoId === 'number' && item.photoId > 0)) &&
    (item.type !== 'event_pass' || (Array.isArray(item.passEntries) && item.passEntries.length > 0)) &&
    (item.isGroup === undefined || typeof item.isGroup === 'boolean')
  );
};

/** Rider identity: federation ID when present, else normalized name (adhoc events). */
export const riderKey = (swissId: string | undefined | null, name: string | undefined | null) =>
  (swissId && swissId.trim()) ? `id:${swissId.trim()}` : `name:${(name || '').trim().toLowerCase()}`;

const itemRiderKey = (item: CartItem) => riderKey(item.swissEquestrianRiderId, item.participantName);

/** Returns the event pass in the cart that covers this item, if any. */
export const findCoveringPass = (items: CartItem[], item: Pick<CartItem, 'participantId' | 'eventId' | 'swissEquestrianRiderId' | 'participantName' | 'isGroup' | 'isWinner'>) => {
  if (item.isGroup || item.isWinner) return undefined;
  return items.find(p =>
    p.type === 'event_pass' &&
    p.eventId === item.eventId &&
    (p.passEntries?.some(e => e.participantId === item.participantId) ||
      itemRiderKey(p) === riderKey(item.swissEquestrianRiderId, item.participantName))
  );
};

export const getCart = (): ShoppingCart => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const cart = JSON.parse(stored);
      const rawItems = cart.items || [];
      const validItems = rawItems.filter(isValidCartItem);
      if (validItems.length !== rawItems.length) {
        console.warn(`Removed ${rawItems.length - validItems.length} invalid items from cart`);
        const cleanCart = { items: validItems, total: calculateTotal(validItems) };
        saveCart(cleanCart);
        return cleanCart;
      }
      return { items: validItems, total: calculateTotal(validItems) };
    }
  } catch (error) {
    console.error('Error loading cart:', error);
    localStorage.removeItem(CART_STORAGE_KEY);
  }
  return { items: [], total: 0 };
};

export const saveCart = (cart: ShoppingCart): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  } catch (error) {
    console.error('Error saving cart:', error);
  }
};

const ph = () => (posthogLib.__loaded ? posthogLib : null);

export const addToCart = (item: Omit<CartItem, 'quantity'>, posthog?: any): ShoppingCart => {
  const cart = getCart();

  if (item.type !== 'event_pass' && findCoveringPass(cart.items, item)) {
    toast.info(i18n.t('eventPass.alreadyIncluded'));
    return cart;
  }

  const existingIndex = cart.items.findIndex(cartItem =>
    cartItem.participantId === item.participantId &&
    cartItem.type === item.type &&
    (item.type !== 'single' || cartItem.photoId === item.photoId)
  );

  if (existingIndex >= 0) return cart;

  cart.items.push({ ...item, quantity: 1 });
  if (posthog) trackAddToCart(posthog, item);

  cart.total = calculateTotal(cart.items);
  saveCart(cart);

  if (item.type === 'collection' && !item.isGroup && !item.isWinner) {
    maybeSuggestEventPass(cart, item);
  }
  return cart;
};

/** When a 2nd collection of the same rider at the same event is added, offer the pass. */
const maybeSuggestEventPass = (cart: ShoppingCart, item: Omit<CartItem, 'quantity'>) => {
  const key = riderKey(item.swissEquestrianRiderId, item.participantName);
  const sameRider = cart.items.filter(i =>
    i.type === 'collection' && !i.isGroup && !i.isWinner &&
    i.eventId === item.eventId && itemRiderKey(i) === key
  );
  if (sameRider.length < EVENT_PASS_MIN_RACES) return;
  const current = sameRider.length * PRICES.COLLECTION;
  if (current <= PRICES.EVENT_PASS) return;
  const savings = current - PRICES.EVENT_PASS;
  ph()?.capture('event_pass_upsell_shown', { event_id: item.eventId, collections: sameRider.length, savings });
  toast(i18n.t('eventPass.upsellTitle', { savings }), {
    description: i18n.t('eventPass.upsellDescription', { name: item.participantName }),
    duration: 10000,
    action: {
      label: i18n.t('eventPass.upsellAction'),
      onClick: () => {
        ph()?.capture('event_pass_upsell_accepted', { event_id: item.eventId, savings });
        addEventPass({
          swissEquestrianRiderId: item.swissEquestrianRiderId,
          participantName: item.participantName,
          eventId: item.eventId,
          eventName: item.eventName,
          entries: sameRider.map(c => ({
            participantId: c.participantId,
            raceId: c.raceId,
            raceCategory: c.raceCategory,
            horseName: c.horseName,
          })),
        });
      },
    },
  });
};

export interface AddEventPassInput {
  swissEquestrianRiderId: string;
  participantName: string;
  eventId: string;
  eventName: string;
  entries: EventPassEntry[];
  source?: string;
}

/**
 * Adds an Event Pass and removes the rider's singles/collections it covers at
 * that event. Shows an Undo toast restoring the replaced items.
 */
export const addEventPass = (input: AddEventPassInput): ShoppingCart => {
  const cart = getCart();
  const key = riderKey(input.swissEquestrianRiderId, input.participantName);
  const already = cart.items.find(i => i.type === 'event_pass' && i.eventId === input.eventId && itemRiderKey(i) === key);
  if (already) return cart;

  const entryIds = new Set(input.entries.map(e => e.participantId));
  const replaced = cart.items.filter(i =>
    i.type !== 'event_pass' && !i.isGroup && !i.isWinner && i.eventId === input.eventId &&
    (entryIds.has(i.participantId) || itemRiderKey(i) === key)
  );
  const before = [...cart.items];
  const first = input.entries[0];
  const pass: CartItem = {
    type: 'event_pass',
    participantId: first.participantId,
    swissEquestrianRiderId: input.swissEquestrianRiderId || '',
    participantName: input.participantName,
    horseName: [...new Set(input.entries.map(e => e.horseName).filter(Boolean))].join(', '),
    raceCategory: 'Event Pass',
    eventName: input.eventName,
    eventId: input.eventId,
    raceId: first.raceId,
    price: PRICES.EVENT_PASS,
    quantity: 1,
    passEntries: input.entries,
  };
  cart.items = [...cart.items.filter(i => !replaced.includes(i)), pass];
  cart.total = calculateTotal(cart.items);
  saveCart(cart);
  ph()?.capture('event_pass_added', { event_id: input.eventId, races: input.entries.length, classes: input.entries.length, replaced: replaced.length, source: input.source ?? 'unknown', price: PRICES.EVENT_PASS });

  if (replaced.length > 0) {
    toast.success(i18n.t('eventPass.mergedTitle'), {
      description: i18n.t('eventPass.mergedDescription', { count: replaced.length }),
      duration: 8000,
      action: {
        label: i18n.t('eventPass.undo'),
        onClick: () => {
          ph()?.capture('event_pass_merge_undo', { event_id: input.eventId });
          saveCart({ items: before, total: calculateTotal(before) });
        },
      },
    });
  } else {
    toast.success(i18n.t('eventPass.addedTitle'));
  }
  return cart;
};

export const removeEventPass = (eventId: string, participantId: string): ShoppingCart => {
  const cart = getCart();
  const had = cart.items.some(i => i.type === 'event_pass' && i.eventId === eventId && i.participantId === participantId);
  cart.items = cart.items.filter(i => !(i.type === 'event_pass' && i.eventId === eventId && i.participantId === participantId));
  cart.total = calculateTotal(cart.items);
  saveCart(cart);
  if (had) ph()?.capture('event_pass_removed', { event_id: eventId, source: 'cart' });
  return cart;
};

export const removeFromCart = (participantId: string, type: CartItemType, photoId?: number): ShoppingCart => {
  const cart = getCart();
  cart.items = cart.items.filter(item =>
    !(item.participantId === participantId &&
      item.type === type &&
      (type !== 'single' || item.photoId === photoId))
  );
  cart.total = calculateTotal(cart.items);
  saveCart(cart);
  return cart;
};

export const updateQuantity = (participantId: string, type: CartItemType, quantity: number, photoId?: number): ShoppingCart => {
  const cart = getCart();
  const itemIndex = cart.items.findIndex(item =>
    item.participantId === participantId &&
    item.type === type &&
    (type !== 'single' || item.photoId === photoId)
  );
  if (itemIndex >= 0) {
    if (quantity <= 0) cart.items.splice(itemIndex, 1);
    else cart.items[itemIndex].quantity = quantity;
  }
  cart.total = calculateTotal(cart.items);
  saveCart(cart);
  return cart;
};

export const clearCart = (): ShoppingCart => {
  const emptyCart = { items: [], total: 0 };
  saveCart(emptyCart);
  return emptyCart;
};

export const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => total + (item.price * item.quantity), 0);
};

export const getCartItemCount = (): number => {
  const cart = getCart();
  return cart.items.reduce((count, item) => count + item.quantity, 0);
};
